import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Initials, Notice, Spinner, TeamAvatar } from '../components/ui';
import { MentionText, MentionTextarea, mentionedIn, type Person } from '../components/Mentions';
import { ApiError } from '../lib/api';
import { useMe } from '../lib/session';
import { timeAgo, useColleagues, useCommunityAction, useFeed, usePostThread, useTaggableOrganisations, type Comment, type Kind, type Post, type Scope } from '../lib/community';
import '../components/Form.css';
import './Page.css';
import './Community.css';

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function Avatar({ name, team, size = 40 }: { name: string; team?: boolean; size?: number }) {
  return team ? <TeamAvatar size={size} /> : <Initials name={name} size={size} />;
}

/**
 * Community on the web: one page with two spaces. The organisation's feed,
 * where leaders post, announce (pinned; everyone is told), give shoutouts and
 * keep things kind; and the EducAitor channel, where EducAitor admins post
 * news and shoutouts for everyone. @ tags a colleague, who is told.
 */
export function CommunityPage() {
  const me = useMe();
  const isAdmin = me.role === 'system_admin';
  // The tab lives in the URL (?space=educaitor), so the menu can link straight to the EducAitor channel.
  const [params, setParams] = useSearchParams();
  const scope: Scope = params.get('space') === 'educaitor' ? 'educaitor' : 'organisation';
  const setScope = (s: Scope) => setParams(s === 'educaitor' ? { space: 'educaitor' } : {}, { replace: true });
  const feed = useFeed(scope);
  const first = feed.data?.pages[0];
  const posts = feed.data?.pages.flatMap((p) => p.data) ?? [];
  const org = first?.organisation ?? me.school_name ?? 'Your organisation';

  return (
    <div className="page comm">
      <header className="comm-header">
        <h1>Community</h1>
        <p className="muted">
          {scope === 'organisation'
            ? `News, ideas and shoutouts for ${org}. Only people in your organisation see it.`
            : isAdmin
              ? 'The EducAitor channel: news and shoutouts from you to everyone using EducAitor. People react; they can’t comment.'
              : 'News, tips and shoutouts from the EducAitor team, for everyone using EducAitor.'}
        </p>
      </header>

      <div className="comm-tabs" role="tablist" aria-label="Which community">
        <button type="button" role="tab" aria-selected={scope === 'organisation'} onClick={() => setScope('organisation')}>
          {org}
        </button>
        <button type="button" role="tab" aria-selected={scope === 'educaitor'} onClick={() => setScope('educaitor')}>
          EducAitor
        </button>
      </div>

      {first?.canPost ? <Composer key={scope} scope={scope} org={org} canAnnounce={first.canAnnounce} agreed={first.agreed} isAdmin={isAdmin} name={me.name} /> : null}

      {feed.isLoading ? (
        <Spinner />
      ) : feed.isError ? (
        <Notice tone="error">We couldn&rsquo;t load the feed. Refresh to try again.</Notice>
      ) : posts.length === 0 ? (
        <div className="comm-empty">
          <p className="comm-empty-title">{scope === 'educaitor' ? 'Nothing from EducAitor yet' : 'Nothing here yet'}</p>
          <p className="muted">
            {scope === 'educaitor'
              ? isAdmin
                ? 'Post the first update: it goes to everyone using EducAitor.'
                : 'News and shoutouts from the EducAitor team will appear here.'
              : 'Start with a welcome, an announcement, or a shoutout for someone who’s done something great this week.'}
          </p>
        </div>
      ) : (
        <div className="comm-feed">
          {posts.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
          {feed.hasNextPage ? (
            <div className="comm-more">
              <Button variant="secondary" onClick={() => feed.fetchNextPage()} loading={feed.isFetchingNextPage}>
                Show older posts
              </Button>
            </div>
          ) : null}
        </div>
      )}
      {first?.hiddenPeople ? (
        <p className="faint comm-hidden-note">
          You&rsquo;ve hidden posts from {first.hiddenPeople === 1 ? 'one person' : `${first.hiddenPeople} people`}. Show them again from Community in the app.
        </p>
      ) : null}
    </div>
  );
}

// ---------- Writing

const KINDS: { k: Kind; label: string; hint: (scope: Scope) => string }[] = [
  { k: 'post', label: 'Post', hint: (s) => (s === 'educaitor' ? 'An update in the EducAitor channel.' : 'Everyone in your organisation can read it and reply.') },
  {
    k: 'shoutout',
    label: 'Shoutout',
    hint: (s) => (s === 'educaitor' ? 'Celebrate an organisation: tag it with @. Everyone is told.' : 'Celebrate colleagues: tag them with @ and they’re told.'),
  },
  {
    k: 'announcement',
    label: 'Announcement',
    hint: (s) => (s === 'educaitor' ? 'Pinned, and everyone using EducAitor is told.' : 'Pinned at the top, and everyone in your organisation is told.'),
  },
];

function Composer({
  scope,
  org,
  canAnnounce,
  agreed,
  isAdmin,
  name,
}: {
  scope: Scope;
  org: string;
  canAnnounce: boolean;
  agreed: boolean;
  isAdmin: boolean;
  name: string;
}) {
  const { post, agree } = useCommunityAction();
  const colleagues = useColleagues();
  // Colleagues in an organisation's feed; organisations in the EducAitor channel (never individual teachers).
  const organisations = useTaggableOrganisations(scope === 'educaitor' && isAdmin);
  const taggable = (scope === 'educaitor' ? organisations.data : colleagues.data) ?? [];
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<Kind>('post');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagged, setTagged] = useState<Person[]>([]);
  const [agreeTicked, setAgreeTicked] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  // The "Posted" note fades after a few seconds.
  useEffect(() => {
    if (message?.tone !== 'success') return;
    const t = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(t);
  }, [message]);

  const kinds = KINDS.filter((x) => x.k !== 'announcement' || canAnnounce);
  const mentions = mentionedIn(content, tagged);
  const postingAs = scope === 'educaitor' ? 'EducAitor' : name;
  const team = scope === 'educaitor' || isAdmin;

  const reset = () => {
    setOpen(false);
    setKind('post');
    setTitle('');
    setContent('');
    setTagged([]);
  };

  const submit = async () => {
    setMessage(null);
    if (!content.trim()) return setMessage({ tone: 'error', text: 'Write something first.' });
    if (kind === 'shoutout' && scope === 'organisation' && !mentions.length) return setMessage({ tone: 'error', text: 'Tag who you’re celebrating with @.' });
    if (!agreed && !agreeTicked) return setMessage({ tone: 'error', text: 'Tick that you agree to the community guidelines.' });
    try {
      if (!agreed) await agree.mutateAsync();
      await post.mutateAsync({ kind, scope, title: kind === 'announcement' ? title.trim() || undefined : undefined, content: content.trim(), mentions });
      reset();
      setMessage({
        tone: 'success',
        text:
          kind === 'announcement'
            ? scope === 'educaitor'
              ? 'Announced to everyone using EducAitor.'
              : `Announced. Everyone in ${org} has been told.`
            : kind === 'shoutout'
              ? 'Shoutout posted, and they’ve been told.'
              : mentions.length
                ? 'Posted, and the people you tagged have been told.'
                : 'Posted.',
      });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'That didn’t post. Try again.' });
    }
  };

  if (!open) {
    return (
      <div className="comm-compose-closed-wrap">
        <button type="button" className="comm-compose-closed" onClick={() => { setOpen(true); setMessage(null); }}>
          <Avatar name={postingAs} team={team} size={36} />
          <span>{scope === 'educaitor' ? 'Post an update for everyone using EducAitor…' : `Share something with ${org}…`}</span>
        </button>
        {message?.tone === 'success' ? <p className="comm-note">{message.text}</p> : null}
      </div>
    );
  }

  return (
    <section className="comm-compose" aria-label="New post">
      <div className="comm-compose-head">
        <Avatar name={postingAs} team={team} size={36} />
        <div>
          <strong>{postingAs}</strong>
          <span className="faint">{KINDS.find((x) => x.k === kind)?.hint(scope)}</span>
        </div>
      </div>
      {kinds.length > 1 ? (
        <div className="comm-kinds" role="radiogroup" aria-label="Kind of post">
          {kinds.map((x) => (
            <button key={x.k} type="button" role="radio" aria-checked={kind === x.k} className="comm-kind" onClick={() => setKind(x.k)}>
              {x.label}
            </button>
          ))}
        </div>
      ) : null}
      {kind === 'announcement' ? (
        <input className="comm-input" placeholder="Headline (optional)" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} aria-label="Headline" />
      ) : null}
      <MentionTextarea
        value={content}
        onChange={setContent}
        people={taggable}
        onTag={(p) => setTagged((t) => (t.some((x) => x.id === p.id) ? t : [...t, p]))}
        maxLength={4000}
        ariaLabel="Your post"
        autoFocus
        className="textarea comm-textarea"
        placeholder={
          kind === 'shoutout'
            ? scope === 'organisation'
              ? 'Who are you celebrating? Type @ to tag them'
              : 'What are you celebrating? Type @ to tag an organisation'
            : kind === 'announcement'
              ? 'What do people need to know?'
              : scope === 'organisation'
                ? 'Share an idea, a resource or a question. Type @ to tag someone'
                : 'What’s new? Type @ to tag an organisation'
        }
      />
      {!agreed ? (
        <label className="auth-check comm-agree">
          <input type="checkbox" checked={agreeTicked} onChange={(e) => setAgreeTicked(e.target.checked)} />
          <span>
            I agree to the community guidelines: be kind and professional, no abusive content, and never share pupils&rsquo; personal details. Reports are acted
            on within 24 hours.
          </span>
        </label>
      ) : null}
      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}
      <div className="comm-compose-foot">
        <div className="comm-compose-actions">
          <Button variant="ghost" onClick={reset}>
            Cancel
          </Button>
          <Button onClick={submit} loading={post.isPending || agree.isPending}>
            {kind === 'announcement' ? 'Announce' : kind === 'shoutout' ? 'Post shoutout' : 'Post'}
          </Button>
        </div>
      </div>
    </section>
  );
}

// ---------- Reading

/** A small "…" menu: pin, hide someone's posts, report, delete or remove. */
function Menu({ items }: { items: { label: string; onClick: () => void; danger?: boolean }[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  if (!items.length) return null;
  return (
    <div className="comm-menu" ref={ref}>
      <button type="button" className="comm-menu-button" aria-label="More options" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span aria-hidden="true">•••</span>
      </button>
      {open ? (
        <div className="comm-menu-list" role="menu">
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              className={it.danger ? 'is-danger' : undefined}
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const KIND_LABEL: Record<Kind, string | null> = { post: null, announcement: 'Announcement', shoutout: 'Shoutout', achievement: 'Achievement' };

function PostCard({ post }: { post: Post }) {
  const act = useCommunityAction();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const first = post.author.name.split(' ')[0];
  const items = [
    ...(post.canPin ? [{ label: post.pinned ? 'Unpin' : 'Pin to the top', onClick: () => act.pin.mutate({ id: post.id, pinned: !post.pinned }) }] : []),
    ...(!post.mine && post.scope === 'organisation'
      ? [
          {
            label: `Hide posts from ${first}`,
            onClick: () => {
              if (window.confirm(`Hide ${post.author.name}’s posts? They won’t be told.`)) act.hide.mutate(post.author.id);
            },
          },
        ]
      : []),
    // Leaders and admins remove posts themselves; everyone else reports them.
    ...(!post.mine && !post.canDelete
      ? [{ label: 'Report to leaders', onClick: () => act.report.mutate({ postId: post.id }, { onSuccess: () => setNote('Reported. Leaders and EducAitor have been told.') }) }]
      : []),
    ...(post.canDelete
      ? [
          {
            label: post.mine ? 'Delete post' : 'Remove post',
            danger: true,
            onClick: () => {
              if (window.confirm(post.mine ? 'Delete your post?' : `Remove ${post.author.name}’s post for everyone?`)) act.remove.mutate(post.id);
            },
          },
        ]
      : []),
  ];
  const kind = KIND_LABEL[post.kind];
  const shoutoutNames = post.kind === 'shoutout' && post.mentions.some((m) => !post.content.includes(`@${m.name}`));
  return (
    <article className={`comm-post${post.kind !== 'post' ? ` is-${post.kind}` : ''}`}>
      {kind || post.pinned ? (
        <p className="comm-post-tag">
          {kind}
          {kind && post.pinned ? ' · ' : ''}
          {post.pinned ? 'Pinned' : ''}
        </p>
      ) : null}
      <div className="comm-post-head">
        <Avatar name={post.author.name} team={post.author.team} />
        <div className="comm-post-who">
          <strong>{post.author.name}</strong>
          <span className="faint">
            {cap(post.author.role)} · {timeAgo(post.createdAt)}
          </span>
        </div>
        <Menu items={items} />
      </div>
      {post.badge ? (
        <p className="comm-badge">
          {post.badge.tier ? `${post.badge.tier} · ` : ''}
          {post.badge.name}
        </p>
      ) : null}
      {post.title ? <h2 className="comm-post-title">{post.title}</h2> : null}
      {post.content ? (
        <p className="comm-post-text">
          <MentionText text={post.content} mentions={post.mentions} />
        </p>
      ) : null}
      {shoutoutNames ? <p className="comm-shoutout-for">For {post.mentions.map((m) => m.name).join(', ')}</p> : null}
      {note ? <p className="comm-note">{note}</p> : null}
      <div className="comm-post-foot">
        <button type="button" className={`comm-react${post.isLiked ? ' is-on' : ''}`} onClick={() => act.like.mutate(post.id)} aria-pressed={post.isLiked}>
          <span aria-hidden="true">{post.isLiked ? '♥' : '♡'}</span> {post.likeCount ? post.likeCount : 'Like'}
        </button>
        {post.canComment ? (
          <button type="button" className="comm-react" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {post.commentCount ? `${post.commentCount} ${post.commentCount === 1 ? 'reply' : 'replies'}` : 'Reply'}
          </button>
        ) : null}
      </div>
      {open ? <Thread postId={post.id} /> : null}
    </article>
  );
}

function Thread({ postId }: { postId: string }) {
  const thread = usePostThread(postId);
  const colleagues = useColleagues();
  const act = useCommunityAction();
  const [text, setText] = useState('');
  const [tagged, setTagged] = useState<Person[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [needsAgree, setNeedsAgree] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setError(null);
    try {
      await act.comment.mutateAsync({ id: postId, content: text.trim(), mentions: mentionedIn(text, tagged) });
      setText('');
      setTagged([]);
      setNeedsAgree(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'That didn’t send. Try again.';
      if (/guidelines/i.test(msg)) setNeedsAgree(true);
      else setError(msg);
    }
  };

  if (thread.isLoading) return <Spinner small />;
  return (
    <div className="comm-thread">
      {(thread.data?.comments ?? []).map((c) => (
        <CommentRow key={c.id} c={c} />
      ))}
      <div className="comm-reply">
        <MentionTextarea
          value={text}
          onChange={setText}
          people={colleagues.data ?? []}
          onTag={(p) => setTagged((t) => (t.some((x) => x.id === p.id) ? t : [...t, p]))}
          maxLength={2000}
          ariaLabel="Write a reply"
          placeholder="Reply, or @ to tag someone"
          className="textarea comm-reply-box"
        />
        <Button onClick={send} loading={act.comment.isPending} disabled={!text.trim()}>
          Reply
        </Button>
      </div>
      {needsAgree ? (
        <div className="comm-agree-inline">
          <p>
            Before your first reply: be kind and professional, no abusive content, and never share pupils&rsquo; personal details. Reports are acted on within 24
            hours.
          </p>
          <Button
            onClick={async () => {
              await act.agree.mutateAsync();
              setNeedsAgree(false);
              send();
            }}
            loading={act.agree.isPending}
          >
            I agree, post my reply
          </Button>
        </div>
      ) : null}
      {error ? <Notice tone="error">{error}</Notice> : null}
    </div>
  );
}

function CommentRow({ c }: { c: Comment }) {
  const act = useCommunityAction();
  const [note, setNote] = useState<string | null>(null);
  const first = c.author.name.split(' ')[0];
  const items = [
    ...(!c.mine
      ? [
          {
            label: `Hide posts from ${first}`,
            onClick: () => {
              if (window.confirm(`Hide ${c.author.name}’s posts and comments? They won’t be told.`)) act.hide.mutate(c.author.id);
            },
          },
        ]
      : []),
    ...(!c.mine && !c.canDelete ? [{ label: 'Report to leaders', onClick: () => act.report.mutate({ commentId: c.id }, { onSuccess: () => setNote('Reported.') }) }] : []),
    ...(c.canDelete
      ? [
          {
            label: c.mine ? 'Delete reply' : 'Remove reply',
            danger: true,
            onClick: () => {
              if (window.confirm('Remove this reply?')) act.removeComment.mutate(c.id);
            },
          },
        ]
      : []),
  ];
  return (
    <div className="comm-comment">
      <Avatar name={c.author.name} team={c.author.team} size={30} />
      <div className="comm-comment-body">
        <p className="comm-comment-head">
          <strong>{c.author.name}</strong> <span className="faint">{timeAgo(c.createdAt)}</span>
        </p>
        <p className="comm-comment-text">
          <MentionText text={c.content} mentions={c.mentions ?? []} />
        </p>
        {note ? <p className="comm-note">{note}</p> : null}
      </div>
      <Menu items={items} />
    </div>
  );
}
